import {
  Component,
  OnDestroy,
  OnInit,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { notifyMessage } from '../../shared/constants/notify-message';
import { Subscription } from 'rxjs';
import { ModalService } from '@purespectrum1/ui/modal';
import { QUALIFICATION_CODES } from '../../shared/constants/qualification-code';
import { QuotaModalComponent } from '../quota-modal/quota-modal.component';
import { QuotaModalService } from '../quota-modal/quota-modal.service';

import { Constants } from '../../shared/constants/create-survey.constants';

import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import { InvoiceOperationService } from '../../shared/services/invoice-operation-services/invoice-operation.service';
import { SelectedQuota } from '../../shared/interfaces/quota.interface';
import { LocationQualificationService } from '../services/qualification/location-service/location.service';
import {
  DownloadLocationFile,
  UploadZipCodeApiResponse,
  ZipCodeInternalData,
} from '../../shared/interfaces/survey.interface';
import {
  SelectedZipcodes,
  LocationGroup,
  QuotaFlags,
  LocationGroupResponse,
  LocationUploadResponseInterface,
  Qualification,
} from '../../shared/interfaces/qualification.interface';
import { Flexible } from '../../shared/interfaces/create-surveys.interface';
import {
  IndividualConfig,
  ToasterService,
} from '@purespectrum1/ui/toaster-service';
import { AuthService } from '../../shared/services/auth/auth.service';
@Component({
  selector: 'ps-ui-upload-location-qualification',
  templateUrl: './upload-location-qualification.component.html',
  styleUrls: ['./upload-location-qualification.component.css'],
})
export class UploadLocationQualificationComponent implements OnInit, OnDestroy {
  readonly QUALIFICATION_CODES = QUALIFICATION_CODES;
  uploadedFilePath!: string;
  public locationTemplate: string = '';
  public haveZipcodeQuotas: boolean = false;
  public selectedZipcodes: SelectedZipcodes[] = [];
  public latestUploads: string[] = [];
  public locationGroups: SelectedQuota[] = [];
  private _subscriptions: Subscription = new Subscription();
  public groupDetail: LocationGroup[] = [];
  @Input() qualification: Qualification = {} as Qualification;
  @Input() selectedCountry!: string;
  @Input() surveyId!: number;
  @Input() complete!: number;
  @Input() clickBalance!: number;
  @Input() quotas?: SelectedQuota[];
  @Input() flexible?: Flexible;
  @Input() deleteQuotas!: EventEmitter<string>;
  @Input() clearLocationData!: EventEmitter<string>;
  @Input() changeCompletes: EventEmitter<number> = new EventEmitter<number>();
  @Input() isQlcLiveSurvey: boolean = false;
  @Input() hasNestedGroup: boolean = false;
  @Output() selectionChange = new EventEmitter();
  @Output() readonly quotaChange = new EventEmitter();

  public quotaFlags: QuotaFlags = {
    add: false,
    edit: false,
    delete: false,
    census: false,
  };

  constructor(
    private _toastr: ToasterService,
    private _locationQualificationsService: LocationQualificationService,
    private _invoiceOperationService: InvoiceOperationService,
    private _auth: AuthService,
    private _modal: ModalService,
    private _quotaModalService: QuotaModalService
  ) {}

  ngOnInit(): void {
    // set default flexibility in case of undefined.
    if (!this.flexible) {
      this.flexible = {
        enable: true,
        value: 0,
      };
    }

    this._setQuotaFlags();
    this._setLatestUploads();
    this._setQuotaOptions();
    if (
      this.selectedCountry === 'CA' &&
      this.qualification.qualification_code === QUALIFICATION_CODES.FSA
    ) {
      this.locationTemplate = `../../../../assets/templates/fsaCodeTemplate.csv`;
    } else {
      this.locationTemplate = `../../../../assets/templates/zipcodesTemplates/zipcode_template_${this.selectedCountry}.csv`;
    }
    this.deleteQuotas.subscribe((msg: string) => {
      this._deleteLocationQuotas();
    });
    this.clearLocationData.subscribe((msg: string) => {
      if (msg == 'ok') {
        this.clearZipcodes();
      }
    });
    this.changeCompletes.subscribe((newCompletes) => {
      if (this.quotas) {
        this.quotas = this._quotaModalService.reDistributeQuotaQuantites(
          this.quotas,
          newCompletes,
          this.flexible
        );
        this.quotaChange.emit({
          msg: Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
          qualification: this.qualification,
          quotas: this.quotas,
          flexible: this.flexible,
        });
      }
    });
  }

  private _setQuotaFlags() {
    this.quotaFlags.add = !this.qualification.quotas?.length;
    this.quotaFlags.edit = !!this.qualification.quotas?.length;
    this.quotaFlags.delete = !!this.qualification.quotas?.length;
  }

  private _setLatestUploads() {
    if (this.qualification.data?.length && !this.qualification.quotas?.length) {
      this.qualification.data.forEach((data) => {
        this.latestUploads.push(data.name || data.id);
      });
    }
  }

  private _setQuotaOptions() {
    if (!this.surveyId) {
      return;
    }
    if (this.qualification.data?.length) {
      this.locationGroups = [];
      const locationSub = this._locationQualificationsService
        .getGroupDetails(this.surveyId, this.qualification.qualification_code)
        .subscribe((response: LocationGroupResponse[]) => {
          this.groupDetail = response;
          if (this.groupDetail?.length && !this.qualification.quotas?.length) {
            this.quotaFlags.add = true;
          }
          //removing location groups with no codes
          this.groupDetail = this.groupDetail.filter(
            (quotas) => quotas.condition_codes?.length !== 0
          );
          this.groupDetail?.forEach((group) => {
            this.locationGroups.push({
              text: group.group_name,
              selected: true,
              options: group.condition_codes,
              number: 0,
              percentage: 0,
              minimum: 0,
              maximum: 0,
              group_name: group.group_name,
            });
          });
        });
      this._subscriptions.add(locationSub);
    }
  }

  uploadLocationFile(event: Event) {
    const target = event.target as HTMLInputElement;
    const file: File = (target.files as FileList)[0];
    const isValidFile = this._validateFileExtention(file);
    if (isValidFile) {
      const fdata = new FormData();
      fdata.append('zipcodes', file);
      const inProgressToastrConfig = {
        disableTimeOut: true,
      } as IndividualConfig;
      const inProgressToastr = this._toastr.info(
        notifyMessage.infoMessage.FILE_UPLOAD_IN_PROCESS,
        '',
        inProgressToastrConfig
      );
      const locationData = this._locationQualificationsService
        .uploadLocationsFile(
          this.selectedCountry,
          this.surveyId,
          fdata,
          this.qualification.qualification_code
        )
        .subscribe(
          (response: UploadZipCodeApiResponse) => {
            this._toastr.clear(inProgressToastr.toastId);
            this._modifyResponse(response);
            this.selectionChange.emit(this.qualification);
          },
          ({
            data: { message = notifyMessage.errorMessage.SERVER_ERROR } = {},
          } = {}) => {
            this._toastr.clear(inProgressToastr.toastId);
            this._toastr.error(message);
          }
        );
      this._subscriptions.add(locationData);
    }
  }

  private _modifyResponse(response: UploadZipCodeApiResponse) {
    // this is named as zipCodeData because it contains the zip code information from the response of api
    let zipCodeData = response.zipcodes;
    const modifiedResponse = this._centrailzeLocationResponse(zipCodeData);
    this.qualification.data = [];
    this._setQualificationData(modifiedResponse);
    this._setFilePath(response);
    this._setQuotas(modifiedResponse);
    this.latestUploads = modifiedResponse.validLocationCodes;
    this._getPresentableResponseToast(modifiedResponse);
  }

  private _setFilePath(response: UploadZipCodeApiResponse) {
    if (this.qualification.qualification_code === QUALIFICATION_CODES.FSA) {
      this.qualification.fsacodeFilePath = response.fsaFilePath;
      this.uploadedFilePath = response.fsaFilePath;
    } else if (
      this.qualification.qualification_code === QUALIFICATION_CODES.ZIPCODE
    ) {
      this.qualification.zipcodeFilePath = response.zipcodeFilePath;
      this.uploadedFilePath = response.zipcodeFilePath;
    }
  }

  private _setQuotas(modifiedResponse: LocationUploadResponseInterface) {
    if (Object.keys(modifiedResponse.groupedLocationCodes).length) {
      this.quotaFlags.add = true;
      this.quotas = [];
      this.locationGroups = [];
      Object.keys(modifiedResponse.groupedLocationCodes).forEach((key) => {
        this.locationGroups.push(
          this._getQuotaObj(
            modifiedResponse.groupedLocationCodes,
            key,
            modifiedResponse.fsaList
          )
        );
      });
    }
  }

  private _getQuotaObj(
    groupDetail: { [key: string]: string[] },
    groupName: string,
    fsaMapping?: {
      id: string;
      name: string;
    }[]
  ) {
    // Removing the duplicates from the options
    const optionsDuplicatesFiltered = [...new Set(groupDetail[groupName])];
    const options = fsaMapping?.length
      ? this._getGroupedOptions(groupDetail, fsaMapping, groupName)
      : optionsDuplicatesFiltered;
    return {
      text: groupName,
      selected: true,
      options,
      number: 0,
      percentage: 0,
      minimum: 0,
      maximum: 0,
      group_name: groupName,
    } as SelectedQuota;
  }

  private _getGroupedOptions(
    groupDetail: { [key: string]: string[] },
    fsaMapping: {
      id: string;
      name: string;
    }[],
    groupName: string
  ) {
    const options: string[] = [];
    groupDetail[groupName].forEach((code) => {
      const codeArr = fsaMapping.filter((fsa) => fsa.name === code);
      if (codeArr?.length) {
        options.push(codeArr[0].id);
      }
    });
    return options;
  }

  private _setQualificationData(
    modifiedResponse: LocationUploadResponseInterface
  ) {
    if (modifiedResponse.validLocationCodes?.length) {
      modifiedResponse.validLocationCodes.forEach((code) => {
        const tempConditionData = modifiedResponse.fsaList?.length
          ? modifiedResponse.fsaList.filter((list) => list.name === code)[0]
          : { id: code, name: code };
        this.qualification.data.push(tempConditionData);
      });
    }
  }

  downloadCurrentLocationFile() {
    const downloadedZipCode = this._locationQualificationsService
      .downloadCurrentLocationFile(this.uploadedFilePath, this.latestUploads)
      .subscribe(
        (response: DownloadLocationFile) => {
          const file = response.fileName;
          window.location.assign(
            this._invoiceOperationService.downloadFile(file, this._auth.token!)
          );
        },
        ({
          data: { message = notifyMessage.errorMessage.SERVER_ERROR } = {},
        } = {}) => {
          this._toastr.error(message);
        }
      );
    this._subscriptions.add(downloadedZipCode);
  }

  clearZipcodes() {
    if (this.qualification?.data.length || this.latestUploads.length) {
      this.qualification.data = [];
      this.latestUploads = [];
      this.quotaFlags.add = false;
      this.uploadedFilePath = '';
      this.selectionChange.emit(this.qualification);
      this._toastr.success(
        `${this.qualification.qualification_name} ${notifyMessage.successMessage.LOCATION_CLEARED}`
      );
    }
  }

  openZipcodesQuotaModal() {
    if (this._quotaModalService.inValidComplete(this.complete)) {
      return;
    }
    const modalRef = this._modal.open(QuotaModalComponent, {
      data: {
        quotas: this.quotas?.length ? this.quotas : this.locationGroups,
        complete: this.complete,
        flexible: this.flexible,
        clickBalance: this.clickBalance,
        qualification: this.qualification,
        hasNestedGroup: this.hasNestedGroup,
      },
      width: '80%',
    });

    modalRef.onClose$.subscribe((response) => {
      if (
        [
          Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
          Constants.QUOTA_MODAL_ACTION.APPLY_NEST_QUOTA,
        ].includes(response?.msg)
      ) {
        this.quotas = response.quotas;
        this.flexible = response.flexible;
        this.quotaFlags = {
          add: false,
          edit: true,
          delete: false,
          clear: true,
        };
        this.quotaChange.emit(response);
      }
    });
  }

  _deleteLocationQuotas() {
    this.quotas = [];
    this.quotaFlags = {
      add: true,
      edit: false,
      delete: false,
    };
    this.quotaChange.emit({
      msg: 'deleteQuota',
      qualification: this.qualification,
      quotas: this.quotas,
      flexible: this.flexible,
    });
  }

  deleteMultiselectInputQuotas() {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: Constants.CONFIRM_MODAL_TEXT.DELETE_QUOTA.replace(
          '<QUALIFICATION_NAME>',
          this.qualification.qualification_name
        ),
      },
    });

    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'ok') {
        this.quotas = [];
        this.quotaFlags = {
          add: true,
          edit: false,
          delete: false,
        };
        this.quotaChange.emit({
          msg: 'deleteQuota',
          qualification: this.qualification,
          quotas: this.quotas,
          flexible: this.flexible,
        });
      }
    });
  }

  private _validateFileExtention(file: File) {
    const validExt = ['.csv'];
    if (!file) {
      this._toastr.error(notifyMessage.errorMessage.FILE_UPLOAD_MISSING);
      return false;
    }
    const fileExt = file.name.substring(file.name.lastIndexOf('.'));
    if (validExt.indexOf(fileExt) < 0) {
      this._toastr.error(notifyMessage.errorMessage.FILE_UPLOAD_MISSING);
      return false;
    }
    return true;
  }

  private _getPresentableResponseToast(
    response: LocationUploadResponseInterface
  ) {
    let valid = this._validateLocationResponse(response);
    let isModified = response.modifiedLocationCodes !== 0;

    if (valid) {
      this._notifyIfAnyModifiedCode(isModified, response);
    } else {
      const errorMessage =
        this.qualification.qualification_code !== QUALIFICATION_CODES.FSA
          ? notifyMessage.errorMessage.INVALID_LOCATION_CODE_FILE
          : notifyMessage.errorMessage.INVALID_FSA_CODE_FILE;
      this._toastr.error(errorMessage);
    }
  }

  // this function compares if there are any modifed location codes and gives the toast
  private _notifyIfAnyModifiedCode(
    isModified: boolean,
    response: LocationUploadResponseInterface
  ) {
    let duplicatedMsg = '';
    if (response.duplicateLocationCodes) {
      duplicatedMsg = `. ${
        response.duplicateLocationCodes
      } ${notifyMessage.successMessage.DUPLICATED_SKIPPED.replace(
        '<QUALIFICATION_NAME>',
        this.qualification.qualification_name
      )}`;
    }

    if (isModified) {
      this._toastr.success(
        `${response.correctLocationCodes} ${notifyMessage.OUT_OF} ${response.totalLocationCodes} ${this.qualification.qualification_name} ${notifyMessage.successMessage.HAS_MODIFIED_LOCATION_CODE}${duplicatedMsg}`
      );
    } else {
      this._toastr.success(
        `${response.correctLocationCodes} ${notifyMessage.OUT_OF} ${response.totalLocationCodes} ${this.qualification.qualification_name} ${notifyMessage.successMessage.RIGHT_LOCATION_CODE}${duplicatedMsg}`
      );
    }
  }

  // to check if the response contains valid, no incorrect and if has modifiedResponse
  // accept duplicated because they're skipped
  private _validateLocationResponse(response: LocationUploadResponseInterface) {
    return (
      response.validLocationCodes.length > 0 && !response.incorrectLocationCodes
    );
  }

  private _centrailzeLocationResponse(zipCodeData: ZipCodeInternalData) {
    // centralised the response of both apis to a common
    const {
      valid,
      total,
      duplicate,
      correct,
      incorrect,
      grouped,
      validZipcodes,
      totalZipcodes,
      duplicateZipcodes,
      correctZipcodes,
      modifiedZipcodes = 0,
      incorrectZipcodes,
      groupedZipCodes,
      incorrectZipCodesList,
      fsaList,
    } = zipCodeData;

    return {
      validLocationCodes: validZipcodes || valid,
      totalLocationCodes: totalZipcodes || total,
      duplicateLocationCodes: duplicateZipcodes || duplicate,
      correctLocationCodes: correctZipcodes || correct,
      incorrectLocationCodes: incorrectZipcodes || incorrect,
      groupedLocationCodes: groupedZipCodes || grouped,
      modifiedLocationCodes: modifiedZipcodes,
      incorrectLocationCodesList: incorrectZipCodesList,
      fsaList,
    };
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
